import { QueryErrorResetBoundary, useQueryClient } from '@tanstack/react-query';
import { ErrorBoundary, ErrorBoundaryProps } from 'react-error-boundary';
import { ErrorInfo, PropsWithChildren, useCallback } from 'react';
import { captureException } from '@sentry/react';

type QueryErrorBoundaryProps = PropsWithChildren &
    Pick<ErrorBoundaryProps, 'onError' | 'onReset'> & {
        FallbackComponent: NonNullable<ErrorBoundaryProps['FallbackComponent']>;
    };

export function QueryErrorBoundary({ children, FallbackComponent, onError, onReset }: QueryErrorBoundaryProps) {
    const queryClient = useQueryClient();

    const handleError = useCallback<NonNullable<ErrorBoundaryProps['onError']>>(
        function handleError(error: Error, info: ErrorInfo) {
            captureException(error, { extra: { ...info } });

            if (onError) {
                onError(error, info);
            }
        },
        [onError]
    );

    const handleReset = useCallback<NonNullable<ErrorBoundaryProps['onReset']>>(
        function handleReset(details) {
            // NOTE: react query has (probably) issues with suspense mode, where an infinite retry is enabled on first reset.
            // We have to remove all inactive queries, this prevents the infinite error-retry behavior.
            queryClient.removeQueries({ type: 'inactive' });

            if (onReset) {
                onReset(details);
            }
        },
        [queryClient, onReset]
    );

    return (
        <QueryErrorResetBoundary>
            <ErrorBoundary FallbackComponent={FallbackComponent} onError={handleError} onReset={handleReset}>
                {children}
            </ErrorBoundary>
        </QueryErrorResetBoundary>
    );
}
